/**
 * Dashboard de Stress Test para Campanhas
 * Testa pipeline real de campanhas usando provider stress-test (sem envio real)
 * Reutiliza 100% da infraestrutura existente
 */

import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Slider } from '@/components/ui/slider';
import { toast } from 'sonner';
import { Switch } from '@/components/ui/switch';
import { 
  Play, 
  Pause, 
  Trash2, 
  Users, 
  Send, 
  Clock, 
  CheckCircle, 
  XCircle, 
  Loader2,
  RefreshCw,
  Zap,
  AlertTriangle,
  TrendingUp,
  Inbox
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  useStressTestInboxes,
  useStressTestCampaigns,
  useCampaignStressTestStats,
  useCampaignStressTestActions,
} from '@/hooks/useCampaignStressTest';

// Buscar tenants para seleção
function useTenants() {
  return useQuery({
    queryKey: ['tenants-for-stress-test'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tenants')
        .select('id, name')
        .in('status', ['active', 'trial'])
        .order('name');
      
      if (error) throw error;
      return data || [];
    },
  });
}

export default function CampaignStressTest() {
  // Estados de seleção
  const [selectedTenantId, setSelectedTenantId] = useState<string>('');
  const [selectedInboxIds, setSelectedInboxIds] = useState<string[]>([]);
  
  // Função para toggle de inbox na seleção múltipla
  const toggleInbox = (inboxId: string) => {
    setSelectedInboxIds(prev =>
      prev.includes(inboxId)
        ? prev.filter(id => id !== inboxId)
        : [...prev, inboxId]
    );
  };
  const [selectedCampaignId, setSelectedCampaignId] = useState<string>('');
  
  // Estados de configuração
  const [contactCount, setContactCount] = useState(1000);
  const [messageContent, setMessageContent] = useState('Teste de stress #{{numero}} - {{current_datetime}}');
  
  // Step 5 - Delay (mesma estrutura do CreateCampaignDialog)
  const [delayRange, setDelayRange] = useState([1000, 3000]); // ms
  const [blockSize, setBlockSize] = useState(5);
  const [blockDelayRange, setBlockDelayRange] = useState([100, 400]); // ms
  const [useContactName, setUseContactName] = useState(true);
  const [verifyWhatsapp, setVerifyWhatsapp] = useState(true);
  
  // Grupo de Blocos - intervalos variáveis para evitar detecção
  const [enableGroupBlock, setEnableGroupBlock] = useState(false);
  const [groupBlockIntervalRange, setGroupBlockIntervalRange] = useState([10, 20]); // Em minutos
  const [groupBlockIntervalUnit, setGroupBlockIntervalUnit] = useState<'minutes' | 'hours'>('minutes');
  const [groupBlockSize, setGroupBlockSize] = useState(20);
  
  // Estados auxiliares
  const [testContactsCount, setTestContactsCount] = useState(0);
  
  // Queries
  const { data: tenants, isLoading: tenantsLoading } = useTenants();
  const { data: inboxes, isLoading: inboxesLoading } = useStressTestInboxes(selectedTenantId);
  const { data: campaigns, isLoading: campaignsLoading, refetch: refetchCampaigns } = useStressTestCampaigns(selectedTenantId);
  const { data: stats } = useCampaignStressTestStats(selectedCampaignId);
  
  // Actions
  const {
    generateContacts,
    createTestCampaign,
    startCampaign,
    pauseCampaign,
    cleanupTestData,
    countTestContacts,
  } = useCampaignStressTestActions();
  
  // Carregar contagem de contatos quando tenant muda
  useEffect(() => {
    if (selectedTenantId) {
      countTestContacts(selectedTenantId).then(setTestContactsCount).catch(() => setTestContactsCount(0));
    }
  }, [selectedTenantId]);
  
  // Handlers
  const handleGenerateContacts = async () => {
    if (!selectedTenantId) {
      toast.error('Selecione um tenant primeiro');
      return;
    }
    
    try {
      const result = await generateContacts.mutateAsync({ 
        tenantId: selectedTenantId, 
        count: contactCount 
      });
      toast.success(`${result} contatos de teste gerados`);
      setTestContactsCount(await countTestContacts(selectedTenantId));
    } catch (error: any) {
      toast.error(`Erro ao gerar contatos: ${error.message}`);
    }
  };
  
  const handleCreateCampaign = async () => {
    if (!selectedTenantId || selectedInboxIds.length === 0) {
      toast.error('Selecione tenant e pelo menos uma inbox');
      return;
    }
    
    if (testContactsCount === 0) {
      toast.error('Gere contatos de teste primeiro');
      return;
    }
    
    try {
      const campaign = await createTestCampaign.mutateAsync({
        tenantId: selectedTenantId,
        inboxIds: selectedInboxIds,
        contactCount: Math.min(contactCount, testContactsCount),
        messageContent,
        delayMinMs: delayRange[0],
        delayMaxMs: delayRange[1],
        blockSize,
        blockDelayMinMs: blockDelayRange[0],
        blockDelayMaxMs: blockDelayRange[1],
        // Grupo de Blocos
        groupBlockEnabled: enableGroupBlock,
        groupBlockIntervalMinMinutes: groupBlockIntervalUnit === 'hours' 
          ? groupBlockIntervalRange[0] * 60 
          : groupBlockIntervalRange[0],
        groupBlockIntervalMaxMinutes: groupBlockIntervalUnit === 'hours' 
          ? groupBlockIntervalRange[1] * 60 
          : groupBlockIntervalRange[1],
        groupBlockSize,
        // Opções extras
        useContactName,
        verifyWhatsapp,
      });
      toast.success(`Campanha criada com ${campaign.total_recipients} recipients`);
      setSelectedCampaignId(campaign.id);
    } catch (error: any) {
      toast.error(`Erro ao criar campanha: ${error.message}`);
    }
  };
  
  const handleStartCampaign = async () => {
    if (!selectedCampaignId) return;
    
    try {
      await startCampaign.mutateAsync(selectedCampaignId);
      toast.success('Campanha iniciada!');
    } catch (error: any) {
      toast.error(`Erro ao iniciar: ${error.message}`);
    }
  };
  
  const handlePauseCampaign = async () => {
    if (!selectedCampaignId) return;
    
    try {
      await pauseCampaign.mutateAsync(selectedCampaignId);
      toast.info('Campanha pausada');
    } catch (error: any) {
      toast.error(`Erro ao pausar: ${error.message}`);
    }
  };
  
  const handleCleanup = async () => {
    if (!selectedTenantId) return;
    
    try {
      const result = await cleanupTestData.mutateAsync(selectedTenantId);
      toast.success(`Limpeza concluída: ${result.campaignsDeleted} campanhas, ${result.contactsDeleted} contatos`);
      setTestContactsCount(0);
      setSelectedCampaignId('');
    } catch (error: any) {
      toast.error(`Erro na limpeza: ${error.message}`);
    }
  };
  
  // Campanha selecionada
  const selectedCampaign = campaigns?.find(c => c.id === selectedCampaignId);
  
  return (
    <div className="space-y-6">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Stress Test - Campanhas</h1>
          <p className="text-muted-foreground">
            Teste o pipeline real de campanhas com provider simulado
          </p>
        </div>
        <Button variant="outline" onClick={() => refetchCampaigns()}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Atualizar
        </Button>
      </div>
      
      <Tabs defaultValue="config" className="space-y-4">
        <TabsList>
          <TabsTrigger value="config">Configuração</TabsTrigger>
          <TabsTrigger value="monitor">Monitoramento</TabsTrigger>
          <TabsTrigger value="history">Histórico</TabsTrigger>
        </TabsList>
        
        {/* Aba Configuração */}
        <TabsContent value="config" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Seleção de Ambiente */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Ambiente</CardTitle>
                <CardDescription>Selecione tenant e inbox para o teste</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Tenant</Label>
                  <Select value={selectedTenantId} onValueChange={setSelectedTenantId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o tenant" />
                    </SelectTrigger>
                    <SelectContent>
                      {tenants?.map(tenant => (
                        <SelectItem key={tenant.id} value={tenant.id}>
                          {tenant.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Inboxes (Provider stress-test)</Label>
                  <p className="text-xs text-muted-foreground mb-2">
                    Selecione uma ou mais inboxes para testar balanceamento
                  </p>
                  
                  {inboxesLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin" />
                    </div>
                  ) : !inboxes?.length ? (
                    <p className="text-sm text-destructive">
                      Crie uma inbox com provider "Simulador de Testes" primeiro
                    </p>
                  ) : (
                    <div className="grid grid-cols-1 gap-2">
                      {inboxes.map((inbox: any) => (
                        <div
                          key={inbox.id}
                          onClick={() => toggleInbox(inbox.id)}
                          className={cn(
                            "p-3 rounded-lg border-2 cursor-pointer transition-all",
                            "hover:border-primary/50 hover:bg-accent/50",
                            selectedInboxIds.includes(inbox.id)
                              ? "border-primary bg-primary/5"
                              : "border-border"
                          )}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <Inbox className={cn(
                                "h-5 w-5",
                                selectedInboxIds.includes(inbox.id) ? "text-primary" : "text-muted-foreground"
                              )} />
                              <div>
                                <p className="font-medium text-sm">{inbox.name}</p>
                                <p className="text-xs text-muted-foreground">{inbox.identifier || 'sem identificador'}</p>
                              </div>
                            </div>
                            {selectedInboxIds.includes(inbox.id) && (
                              <CheckCircle className="h-5 w-5 text-primary" />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Badge mostrando quantas selecionadas */}
                  {selectedInboxIds.length > 0 && (
                    <Badge variant="outline" className="mt-2">
                      {selectedInboxIds.length} inbox(es) selecionada(s) - Peso: {Math.floor(100 / selectedInboxIds.length)}% cada
                    </Badge>
                  )}
                </div>
                
                <Separator />
                
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Contatos de Teste</p>
                    <p className="text-sm text-muted-foreground">
                      {testContactsCount.toLocaleString()} contatos disponíveis
                    </p>
                  </div>
                  <Badge variant={testContactsCount > 0 ? 'default' : 'secondary'}>
                    <Users className="h-3 w-3 mr-1" />
                    {testContactsCount}
                  </Badge>
                </div>
              </CardContent>
            </Card>
            
            {/* Parâmetros do Teste - UI igual ao CreateCampaignDialog */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Delay</CardTitle>
                <CardDescription>Configure delays e comportamento anti-spam</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Quantidade de Contatos */}
                <div className="space-y-3">
                  <Label>Quantidade de Contatos: {contactCount.toLocaleString()}</Label>
                  <Slider
                    value={[contactCount]}
                    onValueChange={([v]) => setContactCount(v)}
                    min={100}
                    max={50000}
                    step={100}
                  />
                </div>

                <Separator />
                
                {/* Delay entre mensagens - Slider */}
                <div className="space-y-3">
                  <Label>Delay entre mensagens: {(delayRange[0] / 1000).toFixed(1)}s - {(delayRange[1] / 1000).toFixed(1)}s</Label>
                  <Slider
                    value={delayRange}
                    onValueChange={setDelayRange}
                    min={500}
                    max={10000}
                    step={100}
                  />
                </div>
                
                {/* Tamanho do bloco - Slider */}
                <div className="space-y-3">
                  <Label>Tamanho do bloco: {blockSize} mensagens</Label>
                  <Slider
                    value={[blockSize]}
                    onValueChange={(v) => setBlockSize(v[0])}
                    min={1}
                    max={20}
                    step={1}
                  />
                </div>
                
                {/* Delay entre blocos - Slider */}
                <div className="space-y-3">
                  <Label>Delay entre blocos: {(blockDelayRange[0] / 1000).toFixed(1)}s - {(blockDelayRange[1] / 1000).toFixed(1)}s</Label>
                  <Slider
                    value={blockDelayRange}
                    onValueChange={setBlockDelayRange}
                    min={50}
                    max={2000}
                    step={50}
                  />
                </div>

                <Separator />

                {/* Grupo de Blocos */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="font-medium">Grupo de Blocos</Label>
                      <p className="text-xs text-muted-foreground">
                        Agrupa envios com intervalos variáveis para evitar detecção
                      </p>
                    </div>
                    <Switch checked={enableGroupBlock} onCheckedChange={setEnableGroupBlock} />
                  </div>

                  {enableGroupBlock && (
                    <div className="space-y-4 p-4 rounded-lg bg-muted/50 border">
                      {/* Intervalo entre grupos - Range slider */}
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <Label>
                            Intervalo entre grupos: {groupBlockIntervalRange[0]} - {groupBlockIntervalRange[1]} {groupBlockIntervalUnit === 'hours' ? 'h' : 'min'}
                          </Label>
                          <div className="flex gap-1">
                            <Button
                              type="button"
                              variant={groupBlockIntervalUnit === 'minutes' ? 'default' : 'outline'}
                              size="sm"
                              className="h-7 px-2 text-xs"
                              onClick={() => {
                                if (groupBlockIntervalUnit !== 'minutes') {
                                  setGroupBlockIntervalRange([
                                    groupBlockIntervalRange[0] * 60,
                                    groupBlockIntervalRange[1] * 60
                                  ]);
                                  setGroupBlockIntervalUnit('minutes');
                                }
                              }}
                            >
                              Min
                            </Button>
                            <Button
                              type="button"
                              variant={groupBlockIntervalUnit === 'hours' ? 'default' : 'outline'}
                              size="sm"
                              className="h-7 px-2 text-xs"
                              onClick={() => {
                                if (groupBlockIntervalUnit !== 'hours') {
                                  setGroupBlockIntervalRange([
                                    Math.max(1, Math.round(groupBlockIntervalRange[0] / 60)),
                                    Math.max(1, Math.round(groupBlockIntervalRange[1] / 60))
                                  ]);
                                  setGroupBlockIntervalUnit('hours');
                                }
                              }}
                            >
                              Horas
                            </Button>
                          </div>
                        </div>
                        <Slider
                          value={groupBlockIntervalRange}
                          onValueChange={setGroupBlockIntervalRange}
                          min={groupBlockIntervalUnit === 'hours' ? 1 : 5}
                          max={groupBlockIntervalUnit === 'hours' ? 24 : 120}
                          step={groupBlockIntervalUnit === 'hours' ? 1 : 5}
                        />
                        <p className="text-xs text-muted-foreground">
                          Sistema escolherá aleatoriamente um intervalo entre esses valores
                        </p>
                      </div>

                      {/* Mensagens por grupo */}
                      <div className="space-y-3">
                        <Label>Mensagens por grupo: {groupBlockSize}</Label>
                        <Slider
                          value={[groupBlockSize]}
                          onValueChange={(v) => setGroupBlockSize(v[0])}
                          min={5}
                          max={200}
                          step={5}
                        />
                        <p className="text-xs text-muted-foreground">
                          Total de mensagens enviadas antes do intervalo do grupo
                        </p>
                      </div>

                      {/* Resumo visual */}
                      <div className="p-3 rounded-md bg-background border text-xs space-y-1">
                        <p className="font-medium text-muted-foreground">Resumo:</p>
                        <p>
                          A cada <span className="font-semibold text-primary">{groupBlockSize}</span> mensagens, 
                          o sistema aguardará entre{' '}
                          <span className="font-semibold text-primary">
                            {groupBlockIntervalRange[0]}-{groupBlockIntervalRange[1]} {groupBlockIntervalUnit === 'hours' ? 'horas' : 'minutos'}
                          </span>{' '}
                          antes de enviar o próximo grupo.
                        </p>
                        <p className="text-muted-foreground">
                          Blocos de {blockSize} msgs com delay de {(delayRange[0]/1000).toFixed(1)}-{(delayRange[1]/1000).toFixed(1)}s entre msgs
                          {' '}e {(blockDelayRange[0]/1000).toFixed(1)}-{(blockDelayRange[1]/1000).toFixed(1)}s entre blocos
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                <Separator />
                
                {/* Usar nome do contato */}
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Usar nome do contato</Label>
                    <p className="text-xs text-muted-foreground">Substitui {'{{nome}}'} pelo nome</p>
                  </div>
                  <Switch checked={useContactName} onCheckedChange={setUseContactName} />
                </div>
                
                {/* Verificar WhatsApp */}
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Verificar WhatsApp</Label>
                    <p className="text-xs text-muted-foreground">Verifica se número tem WhatsApp</p>
                  </div>
                  <Switch checked={verifyWhatsapp} onCheckedChange={setVerifyWhatsapp} />
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Ações */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Ações</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                <Button 
                  onClick={handleGenerateContacts}
                  disabled={!selectedTenantId || generateContacts.isPending}
                >
                  {generateContacts.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Users className="h-4 w-4 mr-2" />
                  )}
                  Gerar {contactCount.toLocaleString()} Contatos
                </Button>
                
                <Button 
                  onClick={handleCreateCampaign}
                  disabled={selectedInboxIds.length === 0 || testContactsCount === 0 || createTestCampaign.isPending}
                  variant="secondary"
                >
                  {createTestCampaign.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4 mr-2" />
                  )}
                  Criar Campanha de Teste
                </Button>
                
                <Button 
                  onClick={handleCleanup}
                  disabled={!selectedTenantId || cleanupTestData.isPending}
                  variant="destructive"
                >
                  {cleanupTestData.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4 mr-2" />
                  )}
                  Limpar Dados de Teste
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Aba Monitoramento */}
        <TabsContent value="monitor" className="space-y-4">
          {/* Seleção de Campanha */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Campanha em Execução</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4">
                <Select value={selectedCampaignId} onValueChange={setSelectedCampaignId}>
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Selecione uma campanha" />
                  </SelectTrigger>
                  <SelectContent>
                    {campaigns?.map(campaign => (
                      <SelectItem key={campaign.id} value={campaign.id}>
                        {campaign.name} - {campaign.status}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {selectedCampaign?.status === 'draft' && (
                  <Button onClick={handleStartCampaign} disabled={startCampaign.isPending}>
                    <Play className="h-4 w-4 mr-2" />
                    Iniciar
                  </Button>
                )}
                
                {selectedCampaign?.status === 'running' && (
                  <Button onClick={handlePauseCampaign} variant="outline" disabled={pauseCampaign.isPending}>
                    <Pause className="h-4 w-4 mr-2" />
                    Pausar
                  </Button>
                )}
                
                {selectedCampaign?.status === 'paused' && (
                  <Button onClick={handleStartCampaign} disabled={startCampaign.isPending}>
                    <Play className="h-4 w-4 mr-2" />
                    Retomar
                  </Button>
                )}
              </div>
              
              {selectedCampaign && (
                <div className="flex items-center gap-2">
                  <Badge variant={
                    selectedCampaign.status === 'running' ? 'default' :
                    selectedCampaign.status === 'completed' ? 'secondary' :
                    selectedCampaign.status === 'paused' ? 'outline' :
                    'secondary'
                  }>
                    {selectedCampaign.status}
                  </Badge>
                  <span className="text-sm text-muted-foreground">
                    {selectedCampaign.total_recipients?.toLocaleString()} recipients
                  </span>
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Métricas em Tempo Real */}
          {stats && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Pendentes</p>
                        <p className="text-2xl font-bold">{stats.pendingCount.toLocaleString()}</p>
                      </div>
                      <Clock className="h-8 w-8 text-muted-foreground" />
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Enviadas</p>
                        <p className="text-2xl font-bold text-green-600">{stats.sentCount.toLocaleString()}</p>
                      </div>
                      <CheckCircle className="h-8 w-8 text-green-600" />
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Falhas</p>
                        <p className="text-2xl font-bold text-destructive">{stats.failedCount.toLocaleString()}</p>
                      </div>
                      <XCircle className="h-8 w-8 text-destructive" />
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Throughput</p>
                        <p className="text-2xl font-bold">{stats.throughputPerSecond.toFixed(1)}/s</p>
                      </div>
                      <TrendingUp className="h-8 w-8 text-muted-foreground" />
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              {/* Barra de Progresso */}
              <Card>
                <CardContent className="pt-6">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span>Progresso</span>
                      <span>{stats.progressPercent.toFixed(1)}%</span>
                    </div>
                    <Progress value={stats.progressPercent} />
                    <p className="text-sm text-muted-foreground text-center">
                      {stats.sentCount + stats.failedCount} de {stats.totalRecipients} processados
                    </p>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
          
          {!selectedCampaignId && (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center text-muted-foreground py-8">
                  <Zap className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Selecione ou crie uma campanha de teste para monitorar</p>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
        
        {/* Aba Histórico */}
        <TabsContent value="history" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Campanhas de Teste Recentes</CardTitle>
              <CardDescription>Últimas 10 campanhas [STRESS-TEST]</CardDescription>
            </CardHeader>
            <CardContent>
              {campaignsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : !campaigns?.length ? (
                <div className="text-center text-muted-foreground py-8">
                  <AlertTriangle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Nenhuma campanha de teste encontrada</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {campaigns.map(campaign => (
                    <div 
                      key={campaign.id}
                      className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer hover:bg-muted/50 transition-colors ${
                        selectedCampaignId === campaign.id ? 'border-primary bg-muted/30' : ''
                      }`}
                      onClick={() => setSelectedCampaignId(campaign.id)}
                    >
                      <div className="flex items-center gap-3">
                        <Badge variant={
                          campaign.status === 'completed' ? 'secondary' :
                          campaign.status === 'running' ? 'default' :
                          campaign.status === 'failed' ? 'destructive' :
                          'outline'
                        }>
                          {campaign.status}
                        </Badge>
                        <div>
                          <p className="font-medium text-sm">{campaign.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(campaign.created_at).toLocaleString('pt-BR')}
                          </p>
                        </div>
                      </div>
                      <div className="text-right text-sm">
                        <p className="font-medium">
                          {campaign.sent_count || 0}/{campaign.total_recipients || 0}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {campaign.failed_count || 0} falhas
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
